/* eslint-disable */
import React, { useState, useEffect } from "react";
import { Form, Input, Select, Button, Row, Col, Card, message, Spin, Space, Tag } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import { SaveOutlined, CloseOutlined, EnvironmentOutlined } from "@ant-design/icons";
import API from "../../api";
import urls from "../../api/urls";
import Heading from "../../components/Heading";
import { getHubList } from "../../services/api/hub";

const { GET_TAT_DETAILS, CREATE_TAT, UPDATE_TAT } = urls;

const CreateTat = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(false);
    const [hubs, setHubs] = useState([]);
    const [hubLoading, setHubLoading] = useState(false);

    const isEdit = !!id;

    const fetchHubs = async () => {
        setHubLoading(true);
        try {
            const hubData = await getHubList();
            setHubs(hubData);
        } catch (error) {
            console.error("Error fetching hubs:", error);
            message.error("Failed to load hubs");
        } finally {
            setHubLoading(false);
        }
    };

    const fetchDetails = async () => {
        setFetching(true);
        try {
            const response = await API.Api(`${GET_TAT_DETAILS}${id}`).get();
            if (response.status && response.response.data) {
                const data = response.response.data.data;
                form.setFieldsValue({
                    fromHubId: data.fromHubId,
                    fromHubName: data.fromHubName,
                    fromState: data.fromState,
                    toHubId: data.toHubId,
                    toHubName: data.toHubName,
                    toState: data.toState,
                    mode: data.mode,
                    duration: data.duration,
                    durationUnit: data.durationUnit,
                    distance: data.distance,
                    distanceUnit: data.distanceUnit,
                });
            } else {
                message.error("Failed to fetch details");
            }
        } catch (error) {
            message.error("Error fetching details");
        } finally {
            setFetching(false);
        }
    };

    useEffect(() => {
        fetchHubs();
        if (isEdit) {
            fetchDetails();
        }
    }, [id]);

    const handleFromHubChange = (value) => {
        const hub = hubs.find(h => h.value === value);
        if (hub) {
            form.setFieldsValue({
                fromHubName: hub.label,
                fromState: hub.state || ""
            });
        }
    };

    const handleToHubChange = (value) => {
        const hub = hubs.find(h => h.value === value);
        if (hub) {
            form.setFieldsValue({
                toHubName: hub.label,
                toState: hub.state || ""
            });
        }
    };

    const onFinish = async (values) => {
        setLoading(true);
        try {
            // Ensure numeric values are converted as per Angular logic
            const payload = {
                ...values,
                fromHubId: Number(values.fromHubId),
                toHubId: Number(values.toHubId),
                duration: Number(values.duration),
                distance: Number(values.distance),
            };

            let response;
            if (isEdit) {
                response = await API.Api(`${UPDATE_TAT}${id}`).patch(payload);
            } else {
                response = await API.Api(CREATE_TAT).post(payload);
            }

            if (response.status) {
                message.success(`TAT Record ${isEdit ? "updated" : "created"} successfully`);
                navigate("/tatManagement");
            } else {
                message.error(response.response.data.message || "Failed to save record");
            }
        } catch (error) {
            message.error("An error occurred while saving");
        } finally {
            setLoading(false);
        }
    };

    // Watch fields for state chip display
    const fromState = Form.useWatch("fromState", form);
    const toState = Form.useWatch("toState", form);

    return (
        <div className="report-container pt-5">
            <div style={{ backgroundColor: "white", padding: "8px 16px", borderRadius: "8px", marginBottom: "16px" }}>
                <Heading
                    title={isEdit ? "Edit TAT Record" : "Add TAT Record"}
                    isBack={true}
                    onBackClick={() => navigate("/tatManagement")}
                />
            </div>

            <Card className="report-card">
                <Spin spinning={fetching}>
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={onFinish}
                        initialValues={{
                            durationUnit: "hour", // Unified with Angular units
                            distanceUnit: "kilometre",
                            mode: "SURFACE"
                        }}
                    >
                        {/* Hidden fields for tracking names and states compatible with payload */}
                        <Form.Item name="fromHubName" hidden><Input /></Form.Item>
                        <Form.Item name="fromState" hidden><Input /></Form.Item>
                        <Form.Item name="toHubName" hidden><Input /></Form.Item>
                        <Form.Item name="toState" hidden><Input /></Form.Item>

                        <Row gutter={24}>
                            <Col xs={24} md={8}>
                                <Form.Item
                                    label="Source HUB"
                                    name="fromHubId"
                                    rules={[{ required: true, message: "Please select source hub" }]}
                                >
                                    <Select
                                        showSearch
                                        placeholder="Select Source Hub"
                                        options={hubs}
                                        loading={hubLoading}
                                        optionFilterProp="label"
                                        onChange={handleFromHubChange}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={8}>
                                <Form.Item
                                    label="Destination HUB"
                                    name="toHubId"
                                    rules={[{ required: true, message: "Please select destination hub" }]}
                                >
                                    <Select
                                        showSearch
                                        placeholder="Select Destination Hub"
                                        options={hubs}
                                        loading={hubLoading}
                                        optionFilterProp="label"
                                        onChange={handleToHubChange}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={8}>
                                <Form.Item
                                    label="Transport Mode"
                                    name="mode"
                                    rules={[{ required: true, message: "Please select mode" }]}
                                >
                                    <Select
                                        placeholder="Select mode"
                                        options={[
                                            { label: "SURFACE", value: "SURFACE" },
                                            { label: "AIR", value: "AIR" }
                                        ]}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        {/* State Chips - Matching Angular UI */}
                        {(fromState || toState) && (
                            <div style={{ marginBottom: 24, padding: "8px 12px", background: "#f9f9f9", borderRadius: 6, display: "flex", gap: "16px" }}>
                                {fromState && (
                                    <Tag color="blue" icon={<EnvironmentOutlined />}>
                                        Source State: <strong>{fromState}</strong>
                                    </Tag>
                                )}
                                {toState && (
                                    <Tag color="cyan" icon={<EnvironmentOutlined />}>
                                        Dest State: <strong>{toState}</strong>
                                    </Tag>
                                )}
                            </div>
                        )}

                        <div style={{ borderTop: "1px solid #f0f0f0", margin: "16px 0", height: 1 }} />

                        <Row gutter={24}>
                            <Col xs={24} md={12}>
                                <Form.Item label="Duration" required>
                                    <div style={{ display: "flex", gap: "8px" }}>
                                        <Form.Item
                                            name="duration"
                                            noStyle
                                            rules={[{ required: true, message: "Enter duration" }]}
                                        >
                                            <Input type="number" placeholder="Value" style={{ flex: 1 }} />
                                        </Form.Item>
                                        <Form.Item name="durationUnit" noStyle>
                                            <Select style={{ width: "120px" }}>
                                                <Select.Option value="hour">Hour</Select.Option>
                                            </Select>
                                        </Form.Item>
                                    </div>
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item label="Distance" required>
                                    <div style={{ display: "flex", gap: "8px" }}>
                                        <Form.Item
                                            name="distance"
                                            noStyle
                                            rules={[{ required: true, message: "Enter distance" }]}
                                        >
                                            <Input type="number" placeholder="Value" style={{ flex: 1 }} />
                                        </Form.Item>
                                        <Form.Item name="distanceUnit" noStyle>
                                            <Select style={{ width: "120px" }}>
                                                <Select.Option value="kilometre">Kilometre</Select.Option>
                                            </Select>
                                        </Form.Item>
                                    </div>
                                </Form.Item>
                            </Col>
                        </Row>

                        <div style={{ marginTop: 24, textAlign: "right" }}>
                            <Space>
                                <Button icon={<CloseOutlined />} onClick={() => navigate("/tatManagement")}>
                                    Cancel
                                </Button>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    loading={loading}
                                    icon={<SaveOutlined />}
                                >
                                    {isEdit ? "Update Record" : "Create TAT Record"}
                                </Button>
                            </Space>
                        </div>
                    </Form>
                </Spin>
            </Card>
        </div>
    );
};

export default CreateTat;
